import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Home, User, Package } from 'lucide-react';
import { useCheckoutDialog } from '../../contexts/CheckoutDialogContext';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';

interface BottomNavItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  path: string;
  badge?: number;
}

interface BottomNavigationProps {
  className?: string;
}

export const BottomNavigation: React.FC<BottomNavigationProps> = ({ className = '' }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isCheckoutDialogOpen } = useCheckoutDialog();
  const isOnline = useOnlineStatus();

  const navItems: BottomNavItem[] = [
    {
      id: 'home',
      label: 'Home',
      icon: Home,
      path: '/',
    },
    {
      id: 'orders',
      label: 'Orders',
      icon: Package,
      path: '/orders',
    },
    {
      id: 'account',
      label: 'Account',
      icon: User,
      path: '/account-settings',
    },
  ].filter((item) => isOnline || item.id !== 'home');

  const isActive = (path: string): boolean => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  // Hide bottom navigation when checkout dialog is open
  if (isCheckoutDialogOpen) {
    return null;
  }

  return (
    <nav
      className={`
        fixed bottom-0 left-0 right-0 z-50
        bg-white/95 backdrop-blur-md border-t border-gray-200
        safe-area-inset-bottom md:hidden
        ${className}
      `}
      style={{
        paddingBottom: 'env(safe-area-inset-bottom)',
        height: 'calc(75px + env(safe-area-inset-bottom))',
      }}
    >
      <div className="flex items-center justify-around h-full px-2">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);

          return (
            <button
              key={item.id}
              onClick={() => handleNavigation(item.path)}
              className={`
                flex flex-col items-center justify-center
                min-w-0 flex-1 py-1 px-1
                transition-all duration-200 ease-in-out
                relative
                ${
                  active
                    ? 'text-blue-600'
                    : 'text-gray-500 hover:text-gray-700 active:text-blue-500'
                }
              `}
              aria-label={item.label}
              role="tab"
              aria-selected={active}
            >
              {/* Icon Container */}
              <div
                className={`
                relative p-1 rounded-lg transition-all duration-200
                ${active ? 'bg-blue-50 scale-110' : 'hover:bg-gray-50 active:scale-95'}
              `}
              >
                <Icon
                  size={20}
                  className={`
                    transition-all duration-200
                    ${active ? 'stroke-2' : 'stroke-1.5'}
                  `}
                />

                {/* Badge for notifications/orders */}
                {item.badge && item.badge > 0 && (
                  <span
                    className="
                    absolute -top-1 -right-1
                    bg-red-500 text-white text-xs
                    rounded-full min-w-[16px] h-4
                    flex items-center justify-center
                    font-medium leading-none
                  "
                  >
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                )}
              </div>

              {/* Label */}
              <span
                className={`
                text-xs font-medium mt-1 leading-none
                transition-all duration-200
                ${active ? 'text-blue-600' : 'text-gray-500'}
              `}
              >
                {item.label}
              </span>

              {/* Active Indicator */}
              {/* {active && (
                <div
                  className="
                  absolute bottom-0 left-1/2 transform -translate-x-1/2
                  w-1 h-1 bg-blue-600 rounded-full
                  animate-pulse
                "
                />
              )} */}
            </button>
          );
        })}
      </div>

      {/* iOS-style home indicator */}
      {/* <div
        className="absolute bottom-1 left-1/2 transform -translate-x-1/2
                   w-32 h-1 bg-gray-300 rounded-full opacity-60"
        style={{ marginBottom: 'env(safe-area-inset-bottom)' }}
      /> */}
    </nav>
  );
};

export default BottomNavigation;
